import { useState } from "react";
import Divider from '@mui/material/Divider';
import Backdrop from '@mui/material/Backdrop';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import { useDialogs } from '@toolpad/core/useDialogs';
import Grid from '@mui/material/Grid2';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNotifications } from '@toolpad/core/useNotifications';
import LinearProgressWithLabel from '../components/linear-progress-with-label';
import TextMetadataElement from '../components/text-metadata-element';
import FilePicker from '../components/file-picker'
import LoadingMetadataTable from '../components/loading-metadata-table'
import { useAuth } from 'oidc-react';
import Constants from '../config/constants';
import { hexToDecimal } from '../utils/color-util';
import { dataToChunks, generateShortCode } from '../utils/util';
import { handleZipDownload, handleCsvDownload, resolveZipName, resolveCsvName } from '../utils/file-util';
import { CertificateService } from '../services/certificate-service';


const NAME_TEMPLATE = 'CFA_certificate_{name}_{code}.jpg';

const COLUMNS = [
	{
		width: 100,
		label: 'Artist Name',
		dataKey: 'artist',
		type: "TEXT",
	},
	{
		width: 100,
		label: 'Select Image',
		dataKey: 'description',
		type: "TEXT",
	},
];

const ARTIST_TEXT_ELEMENT_DEFAULTS = {
	xOffset: 0,
	yOffset: 50,
	font: 'Avenir Next',
	color: {
		hex: '#672b06',
	},
	fontSize: 180,
	style: Constants.STYLE_ALIGN_CENTER | Constants.STYLE_VERTICAL_ALIGN_CENTER | Constants.FONT_STYLE_UNDERLINE,
};

const TITLE_TEXT_ELEMENT_DEFAULTS = {
	xOffset: 0,
	yOffset: 350,
	font: 'Avenir Next',
	color: {
		hex: '#442f2a',
	},
	fontSize: 80,
	style: Constants.STYLE_ALIGN_CENTER | Constants.STYLE_VERTICAL_ALIGN_CENTER | Constants.FONT_STYLE_ITALIC,
};


function CertificatePremiumPageContent({ pathname }) {
	const auth = useAuth();
	const dialogs = useDialogs();
	const notifications = useNotifications();

	const [data, setData] = useState(null);
	const [progress, setProgress] = useState(0);
	const [template, setTemplate] = useState(null);
	const [showLoader, setShowLoader] = useState(false);
	const [contentType, setContentType] = useState(null);
	const [nameTemplate, setNameTemplate] = useState(NAME_TEMPLATE);
	const [titleTextElement, setTitleTextElement] = useState({ ...TITLE_TEXT_ELEMENT_DEFAULTS });
	const [artistTextElement, setArtistTextElement] = useState({ ...ARTIST_TEXT_ELEMENT_DEFAULTS });

	const service = new CertificateService(auth);


	const onImageUpload = (data) => {
		setTemplate(data.content);
		setContentType(data.contentType)
	};

	const onDataUpload = (data) => {
		setData(data);
	};

	const onArtistTextElementUpdate = (element) => {
		setArtistTextElement({
			xOffset: element.xOffset,
			yOffset: element.yOffset,
			font: element.font,
			color: element.color,
			fontSize: element.fontSize,
			style: element.style
		});
	};

	const onTitleTextElementUpdate = (element) => {
		setTitleTextElement({
			xOffset: element.xOffset,
			yOffset: element.yOffset,
			font: element.font,
			color: element.color,
			fontSize: element.fontSize,
			style: element.style
		});
	};

	const handleSubmit = async () => {
		try {
			setShowLoader(true);
			let operationsCompleted = 0;

			const payloads = buildPayloadChunks(data)
				.map(p => service.createMany(p)
					.finally(() => increaseAndSet()));

			increaseAndSet();

			const results = await Promise
				.all(payloads);

			setShowLoader(false);
			handleZipDownload(results, resolveZipName(nameTemplate));

			notifications.show('Certificates generated successfully.', Constants.NOTIFICATION_SUCCESS);

			function increaseAndSet() {
				setProgress(Math.min(100, Math.round(100 * (operationsCompleted++ / payloads.length))));
			}
		} catch (e) {
			notifications.show('Could not generate certificates. ' + e, Constants.NOTIFICATION_ERROR);
		} finally {
			setShowLoader(false);
		}
	};

	const handleUpload = async () => {
		const projectShortCode = await dialogs.prompt('Please specify the project short-code', {
			okText: 'Upload',
			cancelText: 'Cancel',
		});

		if (!projectShortCode) {
			return;
		}

		try {
			setShowLoader(true);
			let operationsCompleted = 0;

			const payloads = buildPayloadChunks(data)
				.map(c => { return { ...c, urlPrefix: projectShortCode } })
				.map(p => service.uploadMany(p)
					.finally(() => increaseAndSet()));

			increaseAndSet();

			const results = (await Promise
				.all(payloads))
				.flatMap(r => r);

			handleCsvDownload(results, resolveCsvName(nameTemplate));

			setShowLoader(false);

			notifications.show('Certificates uploaded successfully.', Constants.NOTIFICATION_SUCCESS);

			function increaseAndSet() {
				setProgress(Math.min(100, Math.round(100 * (operationsCompleted++ / payloads.length))));
			}
		} catch (e) {
			notifications.show('Could not upload certificates. ' + e, Constants.NOTIFICATION_ERROR);
		} finally {
			setShowLoader(false);
		}

	};

	const buildPayloadChunks = (data) => {
		return dataToChunks(data)
			.map(c => {
				return {
					template: {
						content: template,
						contentType: contentType,
					},
					images: c
						.filter(n => n)
						.map(n => {
							return {
								artistName: n.artist,
								filename: nameTemplate
									.replace("{name}", n.artist)
									.replace("{code}", generateShortCode()),
								elements: [{
									class: Constants.ELEMENT_TEXT,
									text: n.artist,
									xOffset: artistTextElement.xOffset,
									yOffset: artistTextElement.yOffset,
									font: artistTextElement.font,
									color: hexToDecimal(artistTextElement.color.hex),
									fontSize: artistTextElement.fontSize,
									style: artistTextElement.style
								}, {
									class: Constants.ELEMENT_TEXT,
									text: n.description,
									xOffset: titleTextElement.xOffset,
									yOffset: titleTextElement.yOffset,
									font: titleTextElement.font,
									color: hexToDecimal(titleTextElement.color.hex),
									fontSize: titleTextElement.fontSize,
									style: titleTextElement.style
								}]
							}
						}),
				};
			});
	};

	return (
		<>
			<Divider sx={{ m: 2 }} />
			<Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={showLoader} >
				<LinearProgressWithLabel value={progress} />
			</Backdrop>
			<Grid container spacing={4} padding={2} columns={{ xs: 2, sm: 6, md: 6, xl: 12 }}>
				<Grid size={6}>
					<FilePicker onUploadImage={onImageUpload} width={565} height={437} />
				</Grid>
				<Grid size={6}>
					<LoadingMetadataTable onUpload={onDataUpload} columns={COLUMNS} />
				</Grid>

				<Grid size={12}>
					<Accordion>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls="certificate-filename-content"
							id="certificate-filename-header"
						>
							Certificate filename template
        					</AccordionSummary>
						<AccordionDetails>
							<TextField value={nameTemplate} label={'Name Template'} onChange={(v) => setNameTemplate(v.target.value)} required fullWidth />
						</AccordionDetails>
					</Accordion>
					<Accordion>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls="artist-style-content"
							id="artist-header"
						>
							Artist name style
        					</AccordionSummary>
						<AccordionDetails>
							<TextMetadataElement
								xOffset={ARTIST_TEXT_ELEMENT_DEFAULTS.xOffset}
								yOffset={ARTIST_TEXT_ELEMENT_DEFAULTS.yOffset}
								font={ARTIST_TEXT_ELEMENT_DEFAULTS.font}
								fontSize={ARTIST_TEXT_ELEMENT_DEFAULTS.fontSize}
								color={ARTIST_TEXT_ELEMENT_DEFAULTS.color}
								style={ARTIST_TEXT_ELEMENT_DEFAULTS.style}
								onUpdate={onArtistTextElementUpdate}
							/>
						</AccordionDetails>
					</Accordion>
					<Accordion>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls="work-title-style-content"
							id="work-title-header"
						>
							Work title style
        					</AccordionSummary>
						<AccordionDetails>
							<TextMetadataElement
								xOffset={TITLE_TEXT_ELEMENT_DEFAULTS.xOffset}
								yOffset={TITLE_TEXT_ELEMENT_DEFAULTS.yOffset}
								font={TITLE_TEXT_ELEMENT_DEFAULTS.font}
								fontSize={TITLE_TEXT_ELEMENT_DEFAULTS.fontSize}
								color={TITLE_TEXT_ELEMENT_DEFAULTS.color}
								style={TITLE_TEXT_ELEMENT_DEFAULTS.style}
								onUpdate={onTitleTextElementUpdate}
							/>
						</AccordionDetails>
					</Accordion>
				</Grid>

				<Grid size={6}>
					<Button type={'submit'} color={'primary'} disabled={!template || !data} onClick={handleSubmit}>
						Download
					</Button>
				</Grid>
				<Grid size={6}>
					<LoadingButton variant="contained" disabled={!template || !data} onClick={handleUpload}>
						Upload
					</LoadingButton>
				</Grid>
			</Grid>
		</>
	);
}

export default CertificatePremiumPageContent;